import { ParsedRecord } from 'src/components/import/ImportParser';
import { locationAddress, locationEdcAccountNumber, locationName, locationZipCode } from '../validators';
import { fromZonedTime } from 'date-fns-tz';
import { parse } from 'date-fns';
import { parseStringToNumberOrUndefined } from 'src/domain/parseNumber';
import { PJM_TZ } from '..';

/**
 * Currently we support only DR programs during import
 */
export const REGISTRATION_PROGRAM_FOR_IMPORT = ['Load Management DR Full', 'Load Management DR Capacity Only'];
export const REGISTRATION_PRODUCT_TYPE_FOR_IMPORT = ['CAPACITY_PERFORMANCE_DR'];

export function isNotEmptyStringHandler<T extends string>(row: ParsedRecord<T>, key: T, extraValidations?: Array<(value: T) => Error | null>) {
    if (row[key].value === '') {
        row[key].error = `"${key}" is required`;
    } else {
        row[key].parsedValue = row[key].value;
        if (extraValidations?.length) {
            extraValidations.forEach(fn => {
                const error = fn(row[key].value);
                if (error) {
                    row[key].error = `Field "${key}": ${error.message}`;
                }
            });
        }
    }

    return row;
}

export function isNumber<T extends string>(row: ParsedRecord<T>, key: T, extraValidationFn?: (value: number) => Error | null) {
    const num = parseStringToNumberOrUndefined(row[key].value);
    if (num === undefined) {
        row[key].error = `"${key}" is not a number`;
    } else {
        row[key].parsedValue = num;
        if (extraValidationFn) {
            const error = extraValidationFn(num);
            if (error) {
                row[key].error = `Field "${key}": ${error.message}`;
            }
        }
    }

    return row;
}

export function isIntegerNumber<T extends string>(row: ParsedRecord<T>, key: T, extraValidationFn?: (value: number) => Error | null) {
    const num = parseStringToNumberOrUndefined(row[key].value);
    if (num === undefined) {
        row[key].error = `"${key}" is not a number`;
        return row;
    }

    if (Math.round(num) !== num) {
        row[key].error = `"${key}" is not an integer number`;
        return row;
    }

    row[key].parsedValue = num;
    if (extraValidationFn) {
        const error = extraValidationFn(num);
        if (error) {
            row[key].error = `Field "${key}": ${error.message}`;
        }
    }
    return row;
}

export function isDateHandlerWithTimezone<T extends string>(row: ParsedRecord<T>, key: T, timeOfDay: 'start' | 'end') {
    if (row[key].value === '') {
        row[key].error = `"${key}" is required`;
        return row;
    }

    try {
        // Parse date as MM/DD/YYYY
        const parsedDate = parse(row[key].value, 'MM/dd/yyyy', new Date());

        if (!parsedDate) {
            row[key].error = `"${key}" is invalid date, use format MM/DD/YYYY`;
            return row;
        }

        // Interpret the date in Eastern timezone
        // Set to start of day (00:00:00) or end of day (23:59:59.000) in Eastern time
        let dateInEastern;
        if (timeOfDay === 'start') {
            // Get start of day in Eastern timezone
            dateInEastern = fromZonedTime(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), 0, 0, 0), PJM_TZ);
        } else {
            // Get end of day in Eastern timezone (23:59:59)
            dateInEastern = fromZonedTime(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), 23, 59, 59, 0), PJM_TZ);
        }

        row[key].parsedValue = dateInEastern.toISOString();

        return row;
    } catch (err: any) {
        row[key].error = err.message || 'Failed to parse date';
        return row;
    }
}

export function isOneOfHandler<T extends string>(row: ParsedRecord<T>, key: T, allowedOptions: Readonly<string[]> | string[]) {
    const val = String(row[key].value).toLowerCase();
    const foundOptionIndex = allowedOptions.map(it => it.toLowerCase()).findIndex(it => it === val);
    if (foundOptionIndex === -1) {
        row[key].error = `"${key}" must be one of: ${JSON.stringify(allowedOptions)}`;
    } else {
        row[key].parsedValue = allowedOptions[foundOptionIndex];
    }

    return row;
}

export function isYesOrNoHandler<T extends string>(row: ParsedRecord<T>, key: T) {
    const _val = String(row[key].value).toLowerCase();

    if (_val === 'yes' || _val === 'y' || _val === 'true') {
        row[key].parsedValue = true;
        return row;
    }

    if (_val === 'no' || _val === 'n' || _val === 'false') {
        row[key].parsedValue = false;
        return row;
    }

    row[key].error = `"${key}" must be one of: Yes/No`;
    return row;
}

export function locationNameHasOnlyAllowedCharacters<T extends string>(val: T) {
    const result = locationName.isValid(val);
    if (result.valid) return null;
    return result.error;
}

export function locationAddressHasOnlyAllowedCharacters<T extends string>(val: T) {
    const result = locationAddress.isValid(val);
    if (result.valid) return null;
    return result.error;
}

export function locationZipCodeHasOnlyAllowedCharacters<T extends string>(val: T) {
    const result = locationZipCode.isValid(val);
    if (result.valid) return null;
    return result.error;
}

export function stringLengthInRange(min: number, max: number) {
    return (val: string) => {
        if (String(val).length < min || String(val).length > max) {
            const errorMessage = min === 0 ? `String length must be less or equal ${max}` : `String length must be in between ${min} and ${max}`;

            return new Error(errorMessage);
        }
        return null;
    };
}

export function numberInRange(min: number, max: number) {
    return (val: number) => {
        if (val < min || val > max) {
            return new Error(`Number must be in between ${min} and ${max}`);
        }
        return null;
    };
}

export function numberIsPositive() {
    return (val: number) => {
        if (val < 0) {
            return new Error(`Number must be > 0`);
        }
        return null;
    };
}

export function edcAccountNumberHasCorrectFormat(edcName: string) {
    return (val: string) => {
        const result = locationEdcAccountNumber.isValid(val, edcName);
        if (result.valid) return null;

        return result.error;
    };
}

export function prettyPrintOptions(options: Readonly<string[]> | string[]) {
    return `${options.map(it => `"${it}"`).join(', ')}`;
}
